import styled, { css } from 'styled-components';

// ------------------------------------------------------------------ //
// Shared redesign primitives: buttons, chips, steppers, ticks, hand
// notes, section heads, hairline rows, meters, sticky bars, checkboxes.
// ------------------------------------------------------------------ //

const btnBase = css`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	font-family: var(--fd);
	font-size: 17px;
	letter-spacing: 0.4px;
	padding: 16px 34px;
	border-radius: var(--r-pill);
	text-decoration: none;
	cursor: pointer;
	border: none;
	text-transform: lowercase;
	transition:
		transform 0.18s,
		box-shadow 0.25s,
		background 0.2s,
		color 0.2s,
		border-color 0.2s;

	&:active {
		transform: scale(0.97);
	}

	&:disabled,
	&.dis {
		opacity: 0.45;
		pointer-events: none;
	}

	/* busy = request in flight: still focusable (unlike disabled, which would
	   drop keyboard focus mid-submit), but inert and visually settled */
	&[aria-busy='true'] {
		pointer-events: none;
		opacity: 0.75;
	}
`;

/** Inline request-in-flight spinner for buttons; inherits the button color. */
export const BtnSpinner = styled.span`
	width: 15px;
	height: 15px;
	flex-shrink: 0;
	border-radius: 50%;
	border: 2px solid currentColor;
	border-top-color: transparent;
	animation: qz-btn-spin 0.7s linear infinite;

	@keyframes qz-btn-spin {
		to {
			transform: rotate(360deg);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		animation-duration: 1.6s;
	}
`;

const btnSmall = css`
	font-size: 14px;
	padding: 11px 22px;
	border-radius: var(--r-pill);
`;

export const BtnTeal = styled.button<{ $small?: boolean }>`
	${btnBase}
	background: var(--teal);
	color: var(--teal-deep);

	&:hover {
		background: var(--teal-soft);
		transform: translateY(-2px);
		box-shadow: 0 14px 36px rgba(15, 62, 50, 0.22);
	}

	${({ $small }) => ($small ? btnSmall : '')}
`;

export const BtnInk = styled.button<{ $small?: boolean }>`
	${btnBase}
	background: var(--ink);
	color: var(--cream);

	&:hover {
		transform: translateY(-2px);
		box-shadow: ${({ theme }) =>
			theme.mode === 'dark'
				? '0 14px 30px rgba(0, 0, 0, 0.55)'
				: '0 14px 30px rgba(var(--ink-rgb), 0.3)'};
	}

	${({ $small }) => ($small ? btnSmall : '')}
`;

export const BtnLine = styled.button<{ $small?: boolean }>`
	${btnBase}
	background: transparent;
	color: var(--ink);
	border: 1.5px solid rgba(var(--ink-rgb), 0.3);

	&:hover {
		border-color: var(--ink);
		transform: translateY(-2px);
	}

	${({ $small }) => ($small ? btnSmall : '')}
`;

// Link-flavored variants (react-router Link / plain anchor get the same look
// via `as` prop: <BtnTeal as={Link} to=...> also works; these exist for
// styled(Link) type comfort at call sites that need it.)

export const ArrowLink = styled.a`
	font-family: var(--fd);
	font-size: 16px;
	color: var(--teal-dark);
	text-decoration: none;
	display: inline-flex;
	align-items: center;
	gap: 10px;
	transition: gap 0.2s;
	text-transform: uppercase;
	cursor: pointer;

	&:hover {
		gap: 16px;
	}
`;

/** Handwritten Caveat note. Sizes per mockup: 24px small, clamp big. */
export const Hand = styled.span<{
	$color?: 'gold' | 'coral' | 'brown' | 'teal' | 'goldOnDark';
	$big?: boolean;
	$rotate?: number;
}>`
	font-family: var(--fh);
	font-weight: 600;
	display: inline-block;
	font-size: ${({ $big }) => ($big ? 'clamp(24px, 2.2vw, 31px)' : '24px')};
	line-height: 1.25;
	color: ${({ $color }) =>
		$color === 'coral'
			? 'var(--coral)'
			: $color === 'brown'
				? 'var(--brown)'
				: $color === 'teal'
					? 'var(--teal)'
					: $color === 'goldOnDark'
						? 'var(--gold)'
						: 'var(--gold-ink)'};
	transform: rotate(${({ $rotate }) => $rotate ?? -1.6}deg);
`;

/** Section head: big Fjalla h2 + hand note, padded to the content column. */
export const SecHead = styled.div`
	padding: 0 var(--pad);
	margin-bottom: 38px;
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 20px;
	flex-wrap: wrap;
	text-align: left;

	h2 {
		font-size: clamp(34px, 4.4vw, 62px);
	}

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--coral);
		font-size: clamp(23px, 2.2vw, 31px);
		transform: rotate(-1.5deg);
	}
`;

/** Full-bleed hairline list row with number, Fjalla title, desc, arrow. */
export const FRow = styled.a`
	display: block;
	text-decoration: none;
	border-top: 1px solid var(--hair);
	transition: background 0.25s;
	cursor: pointer;
	text-align: left;

	&:last-of-type {
		border-bottom: 1px solid var(--hair);
	}

	&:hover {
		background: rgba(var(--ink-rgb), 0.035);
	}

	.in {
		display: grid;
		grid-template-columns: 70px 1fr auto auto;
		align-items: center;
		gap: clamp(14px, 3vw, 44px);
		padding: 34px var(--pad);
	}

	.num {
		font-family: var(--fd);
		font-size: 15px;
		color: var(--mut);
		letter-spacing: 1px;
	}

	h3 {
		font-size: clamp(30px, 4.6vw, 68px);
	}

	.desc {
		font-size: 16.5px;
		color: var(--brown);
		max-width: 400px;
		text-align: right;
	}

	.arr {
		width: 54px;
		height: 54px;
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: 0.3s;
		flex-shrink: 0;
		color: var(--ink);
	}

	&:hover .arr {
		background: var(--teal);
		border-color: var(--teal);
		color: var(--teal-deep);
		transform: rotate(-45deg);
	}

	@media (max-width: 1079px) {
		.in {
			grid-template-columns: 44px 1fr auto;
		}

		.desc {
			display: none;
		}
	}
`;

/** Square checkbox tick used across konfigurator/brzi (30px, teal when on). */
export const KTick = styled.span<{ $on?: boolean }>`
	width: 30px;
	height: 30px;
	border-radius: var(--r-xs);
	border: 2px solid rgba(var(--ink-rgb), 0.35);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: transparent;
	transition: 0.15s;
	cursor: pointer;
	flex-shrink: 0;
	background: transparent;

	${({ $on }) =>
		$on
			? `background: var(--teal);
	border-color: var(--teal);
	color: var(--teal-deep);`
			: ''}

	svg {
		pointer-events: none;
	}
`;

/** Difficulty chip: teal (default/lako), gold (srednje), coral (teško). */
export const KChip = styled.button<{
	$on?: boolean;
	$tone?: 'teal' | 'gold' | 'coral';
}>`
	font-family: var(--fd);
	font-size: 15px;
	letter-spacing: 0.4px;
	padding: 9px 17px;
	border-radius: var(--r-pill);
	border: 1.5px solid rgba(var(--ink-rgb), 0.3);
	color: var(--brown);
	cursor: pointer;
	transition: 0.15s;
	user-select: none;
	text-transform: uppercase;
	display: inline-block;
	background: transparent;

	&:hover {
		border-color: var(--ink);
	}

	${({ $on, $tone, theme }) => {
		if (!$on) return '';
		if ($tone === 'gold')
			return `background: var(--gold); border-color: var(--gold); color: ${
				theme.mode === 'dark' ? '#1B140E' : 'var(--ink)'
			};`;
		if ($tone === 'coral')
			return `background: var(--coral); border-color: var(--coral); color: ${
				theme.mode === 'dark' ? '#1B140E' : '#fff'
			};`;
		return 'background: var(--teal); border-color: var(--teal); color: var(--teal-deep);';
	}}
`;

/** Question-count stepper: minus / value / plus. */
export const KStp = styled.span`
	display: inline-flex;
	align-items: center;
	border: 1.5px solid rgba(var(--ink-rgb), 0.3);
	border-radius: var(--r-pill);
	overflow: hidden;
	background: var(--panel-2);

	button {
		font-family: var(--fd);
		font-size: 20px;
		width: 42px;
		height: 42px;
		border: none;
		background: transparent;
		color: var(--brown);
		cursor: pointer;
		transition: 0.15s;
		line-height: 1;
	}

	button:hover {
		background: rgba(var(--ink-rgb), 0.06);
		color: var(--ink);
	}

	input.n {
		font-family: var(--fd);
		font-size: 19px;
		color: var(--ink);
		width: 44px;
		text-align: center;
		border: none;
		background: transparent;
		padding: 0;
		outline: none;
	}
`;

/** Segmented control (kategorija / potkategorija; pitanje / odgovor). */
export const KSeg = styled.span`
	display: inline-flex;
	align-items: center;
	border: 1.5px solid rgba(var(--ink-rgb), 0.28);
	border-radius: var(--r-pill);
	padding: 3px;
	background: var(--panel-2);

	button {
		font-family: var(--fb);
		font-weight: 700;
		font-size: 13.5px;
		padding: 7px 15px;
		border-radius: var(--r-pill);
		border: none;
		background: transparent;
		color: var(--mut);
		cursor: pointer;
		transition: 0.15s;
	}

	button.on {
		background: var(--teal);
		color: var(--teal-deep);
	}
`;

/** Caps micro-label (13px letterspaced). */
export const KLab = styled.span`
	font-size: 13px;
	font-weight: 700;
	letter-spacing: 1.6px;
	text-transform: uppercase;
	color: var(--mut);
`;

/** Progress meter with a min marker (konfigurator bottom bar). */
export const KMeter = styled.div<{ $ok?: boolean }>`
	width: min(280px, 28vw);
	height: 8px;
	border-radius: var(--r-pill);
	background: rgba(var(--ink-rgb), 0.12);
	position: relative;

	i {
		display: block;
		height: 100%;
		border-radius: var(--r-pill);
		background: ${({ $ok }) => ($ok ? 'var(--teal-dark)' : 'var(--coral)')};
		transition:
			width 0.25s,
			background 0.25s;
		max-width: 100%;
	}

	.mk {
		position: absolute;
		top: -4px;
		bottom: -4px;
		width: 2px;
		background: rgba(var(--ink-rgb), 0.35);
	}

	.mk span {
		position: absolute;
		top: 14px;
		left: 50%;
		transform: translateX(-50%);
		font-size: 13px;
		font-weight: 700;
		color: var(--mut);
		white-space: nowrap;
	}
`;

/** Sticky bottom action bar (konfigurator / brzi). */
export const KBar = styled.div`
	position: sticky;
	bottom: 0;
	z-index: 1940;
	background: ${({ theme }) =>
		theme.mode === 'dark' ? 'rgba(25,27,29,0.93)' : 'rgba(248,240,227,0.94)'};
	backdrop-filter: blur(16px);
	-webkit-backdrop-filter: blur(16px);
	border-top: 1px solid var(--hair);
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 22px;
	padding: 14px var(--pad);
	flex-wrap: wrap;
	margin-top: 60px;
	text-align: left;

	.left {
		display: flex;
		align-items: center;
		gap: 24px;
		flex-wrap: wrap;
	}

	.right {
		display: flex;
		align-items: center;
		gap: 22px;
		flex-wrap: wrap;
	}

	.tot {
		font-family: var(--fd);
		font-size: 21px;
		color: var(--ink);
		text-transform: uppercase;
	}

	.tot em {
		font-style: normal;
		color: var(--coral);
		font-size: 26px;
	}

	.tot.ok em {
		color: var(--teal-dark);
	}

	@media (max-width: 1079px) {
		bottom: calc(var(--bottom-nav-h, 63px) + env(safe-area-inset-bottom));
	}

	@media (max-width: 720px) {
		padding: 10px var(--pad) 12px;
		gap: 8px;

		.left {
			width: 100%;
			gap: 10px 14px;
		}

		.left > ${KMeter} {
			flex: 1;
			min-width: 120px;
			width: auto;
		}

		.right {
			width: 100%;
			justify-content: space-between;
		}

		/* a lone action (brzi kviz's single button) hugs the right edge;
		   pairs (konfigurator's odaberi sve + nastavi) keep space-between */
		.right > :only-child {
			margin-left: auto;
		}
	}
`;

/** Labelled checkbox row (cart consents, odaberi sve). */
export const CkRow = styled.label`
	display: flex;
	gap: 16px;
	align-items: flex-start;
	cursor: pointer;
	text-align: left;

	input {
		position: absolute;
		opacity: 0;
		pointer-events: none;
	}

	.box {
		flex-shrink: 0;
		width: 24px;
		height: 24px;
		border-radius: var(--r-xs);
		border: 2px solid rgba(var(--ink-rgb), 0.3);
		display: flex;
		align-items: center;
		justify-content: center;
		color: transparent;
		transition: 0.15s;
		margin-top: 2px;
	}

	input:checked + .box {
		background: var(--teal);
		border-color: var(--teal);
		color: var(--teal-deep);
	}

	p {
		font-size: 15.5px;
		color: var(--brown);
		line-height: 1.55;
		margin: 0;
	}

	p a {
		color: var(--teal-dark);
		font-weight: 600;
	}
`;

/** Page heading block: oversized Fjalla h1 (+em accent) with hand note. */
export const PageHead = styled.div`
	padding: 150px var(--pad) 34px;
	text-align: left;

	h1 {
		font-size: clamp(46px, 7vw, 104px);
		line-height: 0.94;
	}

	h1 em {
		font-style: normal;
		color: var(--coral);
	}

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(24px, 2.2vw, 31px);
		display: inline-block;
		margin-top: 10px;
		transform: rotate(-1.4deg);
	}
`;

/** Ruled-paper panel (steps, gratis block). */
export const Paper = styled.div`
	margin: 110px var(--gut) 0;
	background: var(--paper);
	border-radius: var(--r);
	padding: 90px 0 100px;
	position: relative;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		opacity: ${({ theme }) => (theme.mode === 'dark' ? 0.9 : 0.55)};
		pointer-events: none;
		background: repeating-linear-gradient(
			180deg,
			transparent 0 44px,
			rgba(var(--ink-rgb), 0.05) 44px 45px
		);
	}
`;

/** Spec strip: hairline cells with caps label + Fjalla value. */
export const SpecBar = styled.div`
	display: flex;
	align-items: stretch;
	border-bottom: 1px solid var(--hair);
	margin: 44px 0 0;
	text-align: left;

	/* full --pad on every cell starves content on wide screens (the
	   (100vw - 1440px)/2 gutter term multiplied across 8 paddings), so
	   inner edges get a capped pad and only the bar's outer edges keep
	   the page gutter */
	.cell {
		flex: 1;
		padding: 24px min(var(--pad), 48px);
		border-top: 1px solid var(--hair);
		border-right: 1px solid var(--hair);
		min-width: 150px;
	}

	.cell:first-child {
		padding-left: var(--pad);
	}

	.cell:last-child {
		padding-right: var(--pad);
	}

	.cell:last-child {
		border-right: none;
	}

	.k {
		font-size: 13px;
		font-weight: 700;
		letter-spacing: 2.2px;
		text-transform: uppercase;
		color: var(--mut);
		display: block;
		margin-bottom: 6px;
	}

	.v {
		font-family: var(--fd);
		font-size: clamp(19px, 1.8vw, 26px);
		color: var(--ink);
		text-transform: uppercase;
	}

	.v.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--teal-dark);
		font-size: clamp(24px, 2vw, 30px);
		text-transform: none;
	}

	.v.price {
		color: var(--coral);
	}

	.lvl {
		display: inline-flex;
		gap: 5px;
		margin-left: 12px;
		vertical-align: 2px;
	}

	/* težina cell: value and level dots on one centered flex row */
	.cell.flex .v {
		display: inline-flex;
		align-items: center;
		gap: 12px;
	}

	.cell.flex .lvl {
		margin-left: 0;
		vertical-align: baseline;
	}

	/* same voice as the cart-summary difficulty dots: teal = easy, gold =
	   medium, coral = hard; the two unpicked levels stay visible dimmed so
	   position is read too, not just color */
	.lvl i {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: rgba(var(--ink-rgb), 0.15);
	}

	.lvl i.l1.f {
		background: var(--logo);
	}

	.lvl i.l2.f {
		background: var(--gold);
	}

	.lvl i.l3.f {
		background: var(--coral);
	}

	@media (max-width: 1079px) {
		flex-wrap: wrap;

		/* wrapped rows: uniform padding so cells in both rows line up */
		.cell,
		.cell:first-child,
		.cell:last-child {
			min-width: 45%;
			padding: 24px var(--pad);
		}
	}
`;

/** Labeled share trigger: muted icon+label that lighten on hover (QNote-like). */
export const ShareTrigger = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	color: var(--mut);
	font-family: var(--fb);
	font-size: 17.5px;
	transition: color 0.15s;

	&:hover {
		color: var(--ink);
	}

	svg {
		flex-shrink: 0;
	}
`;

/** Desktop share popover: option rows + QR side column, anchored to the
    labeled ShareButton. $placement flips it above/below the trigger. */
export const SharePopover = styled.div<{ $placement: 'up' | 'down' }>`
	position: absolute;
	${({ $placement }) =>
		$placement === 'up'
			? 'bottom: calc(100% + 14px);'
			: 'top: calc(100% + 14px);'}
	left: 50%;
	transform: translateX(-50%);
	z-index: 60;
	display: flex;
	gap: 6px;
	/* abspos shrink-to-fit would clamp to the narrow trigger wrapper and
	   squeeze the rows; size to content instead */
	width: max-content;
	padding: 14px;
	background: var(--panel-2);
	border: 1px solid var(--hair);
	border-radius: 27px;
	box-shadow: ${({ theme }) =>
		theme.mode === 'dark'
			? '0 18px 50px rgba(0, 0, 0, 0.5)'
			: '0 18px 50px rgba(27, 20, 14, 0.16)'};
	animation: qz-share-pop 0.18s ease;

	@keyframes qz-share-pop {
		from {
			opacity: 0;
			transform: translateX(-50%)
				translateY(
					${({ $placement }) => ($placement === 'up' ? '6px' : '-6px')}
				);
		}
		to {
			opacity: 1;
			transform: translateX(-50%) translateY(0);
		}
	}

	&::after {
		content: '';
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		border: 9px solid transparent;
		${({ $placement }) =>
			$placement === 'up'
				? 'top: 100%; border-top-color: var(--panel-2);'
				: 'bottom: 100%; border-bottom-color: var(--panel-2);'}
	}

	.opts {
		min-width: 220px;
		padding-right: 12px;
	}

	.row {
		display: flex;
		align-items: center;
		gap: 13px;
		width: 100%;
		padding: 11px 14px;
		border: none;
		background: none;
		cursor: pointer;
		border-radius: var(--r-ctl);
		color: var(--ink);
		font-family: var(--fb);
		font-size: 15.5px;
		font-weight: 500;
		line-height: 1.2;
		text-align: left;
		text-decoration: none;
		white-space: nowrap;
	}

	.row:hover {
		background: rgba(var(--ink-rgb), 0.06);
	}

	.row:focus {
		outline: none;
	}

	/* icons stay ink like the labels; only the copy label carries brand color */
	.row svg {
		width: 17px;
		height: 17px;
		flex-shrink: 0;
		color: var(--ink);
	}

	.row.copy,
	.row.copy svg {
		color: var(--teal-dark);
	}

	.row .done {
		margin-left: auto;
		font-family: var(--fh);
		font-weight: 600;
		font-size: 20px;
		line-height: 0;
		color: var(--coral);
		transform: rotate(-3deg);
		opacity: 0;
		transition: opacity 0.15s;
	}

	.row.copied .done {
		opacity: 1;
	}

	.sep {
		display: block;
		height: 1px;
		background: var(--hair);
		margin: 8px 10px;
	}

	.qr {
		border-left: 1px solid var(--hair);
		padding: 10px 10px 10px 20px;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 10px;
	}

	/* QR stays dark-on-light in both themes so phones can scan it */
	.qr svg {
		width: 118px;
		height: 118px;
		border-radius: 10px;
		background: ${({ theme }) => (theme.mode === 'dark' ? '#F7F7F7' : '#fff')};
		padding: 8px;
	}

	.qr .cap {
		font-family: var(--fh);
		font-weight: 600;
		font-size: 20px;
		line-height: 1.1;
		color: var(--gold-ink);
		text-align: center;
		transform: rotate(-1.5deg);
		max-width: 140px;
	}
`;

/**
 * Marquee track: children rendered twice for a seamless loop. No gap on the
 * track itself — mobile renders a roll of joined tickets (RollCard) whose
 * dashed right edges draw the junctions, and the desktop SaleCard slots
 * space themselves with margins so every slot stays the same width and the
 * loop wraps at exactly half the scroll width.
 */
export const Marquee = styled.div<{ $reverse?: boolean; $duration?: number }>`
	display: flex;
	width: max-content;
	will-change: transform;
	animation: ${({ $reverse }) => ($reverse ? 'qz-mqr' : 'qz-mq')}
		${({ $duration, $reverse }) => $duration ?? ($reverse ? 52 : 46)}s linear
		infinite;

	&:hover {
		animation-play-state: paused;
	}

	${({ $reverse }) => ($reverse ? 'margin-top: 16px;' : '')}

	@keyframes qz-mq {
		from {
			transform: translateX(0);
		}
		to {
			transform: translateX(-50%);
		}
	}

	@keyframes qz-mqr {
		from {
			transform: translateX(-50%);
		}
		to {
			transform: translateX(0);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

// ------------------------- loading skeletons ------------------------- //

/**
 * Shimmer block for loading placeholders. Size it from the outside (height /
 * aspect-ratio / grid placement); tone and sweep stay consistent everywhere.
 */
export const Sk = styled.div`
	border-radius: var(--r-card);
	background: linear-gradient(
		-90deg,
		rgba(var(--ink-rgb), 0.06) 0%,
		rgba(var(--ink-rgb), 0.11) 50%,
		rgba(var(--ink-rgb), 0.06) 100%
	);
	background-size: 400% 100%;
	animation: qz-sk 1.4s ease-in-out infinite;

	@keyframes qz-sk {
		from {
			background-position: 0% 0%;
		}
		to {
			background-position: -135% 0%;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;
